import type { CountryCodesForRequiredStates } from '@hotjar/country-lib';
import { SubscriptionAmendmentOrderService } from '~/subscription-management/services/amend-order-service';
import { SubscriptionCreationOrderService } from '~/subscription-management/services/create-order-service';
import { SubscriptionReactivationOrderService } from '~/subscription-management/services/reactivate-order-service';
import { countriesWithRequiredStates } from '~/subscription-management/constants/countries';
import { customerHasFairDowngrade } from '~/subscription-management/utils/customer.utils';
import type { User } from '../../../api';
import type { Currency, Customer } from '../../../api/common/model/customer';
import type { SubscriptionStatus } from '../../../api/common/model/subscription';
import type { Context } from '../checkout.machine.types';
import { DEFAULT_COST, getCostsContext } from './events';
import { getAccountDiscounts } from '../../../utils/discounts';

const getOrderService = (accountId: number, status: SubscriptionStatus | undefined) => {
  switch (status) {
    case 'ACTIVE':
      return new SubscriptionAmendmentOrderService(accountId);
    case 'CANCELLED':
      return new SubscriptionReactivationOrderService(accountId);
    default:
      return new SubscriptionCreationOrderService(accountId);
  }
};

export const getDefaultContext = (customer: Customer | null, user: User): Context => {
  const country = customer?.billingAddress?.country ?? user.country ?? 'US';
  const countryDefaults = countriesWithRequiredStates[country as CountryCodesForRequiredStates];
  const hasActiveSubscription = customer?.subscription.status === 'ACTIVE';
  const hasCancelledSubscription = customer?.subscription.status === 'CANCELLED';
  const hasOverdueSubscription = customer?.subscription.status === 'OVERDUE';
  const hasFairDowngrade = customerHasFairDowngrade(customer);
  const orderService = getOrderService(user.accountId, customer?.subscription.status);
  const paymentMethod = customer?.paymentMethod;

  return {
    billingPeriod: customer?.subscription.billingPeriod ?? 'MONTHLY',
    allowancesToAdd: [],
    allowancesToRemove: [],
    allEffectiveAllowances: [],
    allRenewedAllowances: [],
    customer: {
      companyName: customer?.companyName ?? user.companyName ?? '',
      additionalEmailAddresses: customer?.additionalEmailAddresses ?? [],
      currency: customer?.currency ?? (user.currency.toUpperCase() as Currency) ?? 'EUR',
      billingAddress: customer?.billingAddress ?? {
        zipCode: countryDefaults?.zipCode ?? null,
        country,
        emailAddress: user.emailAddress ?? '',
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        state: countryDefaults?.state ?? null,
      },
      paymentMethod,
      currentPeriodCostSummary: customer?.currentPeriodCostSummary,
      taxNumber: customer?.taxNumber,
    },
    /**
     * NOTE: we need to check hasActiveSubscription because of a bug in the backend where a
     * cancelled subscription still returns the active costs in its currentPeriodCostSummary
     * TODO: remove when https://hotjar.atlassian.net/browse/MP-2122 is done
     */
    costs: getCostsContext(
      hasActiveSubscription ? customer?.currentPeriodCostSummary : DEFAULT_COST,
    ),
    orderProcessed: false,
    hasPaymentOrderFailure: false,
    hasOtherOrderFailure: false,
    userId: user.userId,
    accountId: user.accountId,
    customerId: customer?.identifier,
    hasActiveSubscription,
    hasCancelledSubscription,
    hasOverdueSubscription,
    hasFairDowngrade,
    isChurning: false,
    isOnTrial: false,
    isOnTrialWithBasicAllowances: false,
    loadedFromDraftOrder: false,
    canProceed: false,
    subscription: customer?.subscription,
    getCostPreview: (data) => orderService.preview(data),
    processOrder: (data) => orderService.process(data),
    currentPeriodCostSummary: customer?.currentPeriodCostSummary ?? null,
    completedSteps: [],
    accountDiscounts: getAccountDiscounts(
      customer?.currentPeriodCostSummary?.purchasedProductCosts,
      false,
      customer?.subscription,
    ),
  };
};
